import { useState } from "react";
import axios from "axios";
import { Mail, ArrowRight, Check } from "lucide-react";
import Seo from "../components/Seo";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill name, email, and message.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, {
                name: form.name,
                email: form.email,
                company: form.company || null,
                message: form.message,
                source: "contact-page",
            });
            setDone(true);
            toast.success("Message sent. We'll get back within 1 business day.");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="contact-page" className="min-h-screen">
            <Seo
                title="Contact"
                path="/contact"
                description="Get in touch with ezBuildABot. Tell us what you want to automate or build with AI — we reply to every message within one business day."
            />            <div className="relative pt-32 sm:pt-40 pb-16">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div
                    className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{
                        backgroundImage:
                            "url('https://images.unsplash.com/photo-1728330458318-70438beffc44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjeWJlcnB1bmslMjBjaXR5JTIwbmlnaHQlMjBibHVlfGVufDB8fHx8MTc3ODE3NTQ0NXww&ixlib=rb-4.1.0&q=85')",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        maskImage: "linear-gradient(to bottom, black 0%, transparent 80%)",
                        WebkitMaskImage:
                            "linear-gradient(to bottom, black 0%, transparent 80%)",
                    }}
                />
                <div className="relative max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
                    <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#00f0ff]">
                        Get in touch
                    </span>
                    <h1 className="font-heading text-5xl sm:text-7xl font-black tracking-tighter mt-4 leading-[0.95]">
                        Let&apos;s build{" "}
                        <span className="text-gradient-cyan">something ez</span>.
                    </h1>
                    <p className="text-lg text-[#94a3b8] mt-7 max-w-2xl mx-auto">
                        Tell us what you&apos;re trying to automate. We reply to every message
                        within one business day — usually faster.
                    </p>
                </div>
            </div>

            <section className="relative py-12 pb-24">
                <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Contact form */}
                    <div className="lg:col-span-7 rounded-2xl bg-[#0a1325] border border-white/10 p-8 sm:p-10 relative overflow-hidden">
                        <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-[#00f0ff]/10 blur-3xl pointer-events-none" />
                        {!done ? (
                            <form onSubmit={onSubmit} className="relative space-y-5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <Label className="text-xs uppercase tracking-wider text-[#94a3b8]">
                                            Name
                                        </Label>
                                        <Input
                                            data-testid="contact-input-name"
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            placeholder="Alex Rivera"
                                            className="bg-[#050b14] border-white/10 text-white placeholder:text-[#475569] focus-visible:ring-[#00f0ff] focus-visible:border-[#00f0ff]"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-xs uppercase tracking-wider text-[#94a3b8]">
                                            Email
                                        </Label>
                                        <Input
                                            type="email"
                                            data-testid="contact-input-email"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            placeholder="alex@company.com"
                                            className="bg-[#050b14] border-white/10 text-white placeholder:text-[#475569] focus-visible:ring-[#00f0ff] focus-visible:border-[#00f0ff]"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs uppercase tracking-wider text-[#94a3b8]">
                                        Company <span className="text-[#475569] normal-case">(optional)</span>
                                    </Label>
                                    <Input
                                        data-testid="contact-input-company"
                                        value={form.company}
                                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                                        placeholder="Solopreneur, Acme Inc."
                                        className="bg-[#050b14] border-white/10 text-white placeholder:text-[#475569] focus-visible:ring-[#00f0ff] focus-visible:border-[#00f0ff]"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs uppercase tracking-wider text-[#94a3b8]">
                                        How can we help?
                                    </Label>
                                    <Textarea
                                        rows={6}
                                        data-testid="contact-input-message"
                                        value={form.message}
                                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                                        placeholder="What are you trying to automate, build, or improve?"
                                        className="bg-[#050b14] border-white/10 text-white placeholder:text-[#475569] focus-visible:ring-[#00f0ff] focus-visible:border-[#00f0ff] resize-none"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="contact-submit-btn"
                                    className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-3.5 rounded-lg bg-[#00f0ff] text-[#050b14] font-bold hover:bg-white disabled:opacity-60 transition-colors"
                                >
                                    {submitting ? "Sending…" : "Send message"}
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </form>
                        ) : (
                            <div data-testid="contact-success" className="relative text-center py-10">
                                <div className="mx-auto w-16 h-16 rounded-full bg-[#00f0ff]/15 border border-[#00f0ff]/40 flex items-center justify-center mb-5">
                                    <Check className="w-7 h-7 text-[#00f0ff]" strokeWidth={2.5} />
                                </div>
                                <h3 className="font-heading text-2xl font-bold mb-2">
                                    Message received.
                                </h3>
                                <p className="text-[#94a3b8] max-w-sm mx-auto">
                                    Thanks {form.name.split(" ")[0]} — we&apos;ll be back in your
                                    inbox within one business day.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Contact channels */}
                    <aside className="lg:col-span-5 space-y-4">
                        <ContactChannel
                            Icon={Mail}
                            title="Email"
                            value="info@ezbuildabot.com"
                            note="Reply within 1 business day"
                            testId="contact-channel-email"
                        />
                    </aside>
                </div>
            </section>
        </main>
    );
}

function ContactChannel({ Icon, title, value, note, testId }) {
    return (
        <div
            data-testid={testId}
            className="group rounded-2xl bg-[#0a1325] border border-white/10 hover:border-[#00f0ff]/40 p-6 transition-all duration-300 cursor-pointer"
        >
            <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#00f0ff]/10 border border-[#00f0ff]/30 flex items-center justify-center shrink-0 group-hover:bg-[#00f0ff]/20 transition-colors">
                    <Icon className="w-5 h-5 text-[#00f0ff]" />
                </div>
                <div>
                    <div className="text-xs uppercase tracking-wider text-[#94a3b8] mb-1">
                        {title}
                    </div>
                    <div className="font-heading text-lg font-semibold text-white group-hover:text-[#00f0ff] transition-colors">
                        {value}
                    </div>
                    <div className="text-xs text-[#64748b] mt-1">{note}</div>
                </div>
            </div>
        </div>
    );
}
